import * as S from "../../../lib/sales";

export type DocKind = "proposals" | "estimates" | "invoices" | "credit-notes";

/**
 * Which kinds have a public link and a thread, and what the API calls them.
 *
 * A credit note is missing on purpose rather than by oversight: it corrects an
 * invoice that was already shared, and there is nothing for a client to open,
 * answer or ask about on a document that only exists to cancel money. It is
 * also never mailed with the one-click send, which uses the same names.
 */
export const SHARE_TYPE: Partial<Record<DocKind, S.SalesShareType>> = {
  proposals: "proposal",
  estimates: "estimate",
  invoices: "invoice",
};

/** The one document shape the editor, the list and the detail page all work with. */
export type AnyDoc = S.Proposal | S.Estimate | S.Invoice | S.CreditNote;

export interface DocConfig {
  kind: DocKind;
  /** i18n key for the section heading (plural). */
  titleKey: string;
  /** The statuses this kind allows (the list filter and the editor's choices). */
  statuses: string[];
  hasTitle: boolean;
  hasValidUntil: boolean;
  hasIssueDate: boolean;
  hasDueDate: boolean;
  hasPayments: boolean;
  /**
   * Whether this kind can carry a recurrence schedule.
   *
   * A column, like `hasProject`: the eight `recurring*` fields exist on
   * `Invoice` and on none of the other three. A proposal or an estimate is an
   * offer, which is answered once; a credit note reverses a specific invoice.
   * Only a bill is a thing you send again next month.
   */
  hasRecurrence: boolean;
  /**
   * Whether this kind can be linked to a project.
   *
   * A column, not a preference: `projectId` exists on `Estimate` and `Invoice`
   * and on neither of the other two. A proposal reaches a project only by being
   * converted (the estimate it becomes carries the link), and a credit note
   * inherits its place from the invoice it credits.
   */
  hasProject: boolean;
  /**
   * Whether this kind can START a project: a won quote becomes the piece of work
   * it was won for, through `POST /estimates/:id/create-project`.
   *
   * Deliberately not the same flag as `hasProject`. An invoice carries a
   * `projectId` too, but it is BILLING for work that already exists - the
   * project is what produced the invoice, not the other way round - and there is
   * no endpoint on the other three kinds to call. This flag says "the API has a
   * door here", `hasProject` says "the column exists".
   *
   * The action it turns on answers to `manage:crm.projects`, not to the
   * `manage:crm.sales` that opens this whole section, so the config flag is only
   * half the gate; see `DocDetailActions`.
   */
  canStartProject: boolean;
  canSend: boolean;
  /**
   * Send can also open the mail composer with this document's PDF attached, so
   * the operator writes the cover letter (`/email?estimate=<id>`). Only the
   * estimate has the server routes for it today.
   */
  mailCompose?: boolean;
  /** The convert action (a proposal becomes an estimate, an estimate an invoice). */
  convert?: { labelKey: string; run: (id: string) => Promise<{ ok: boolean; estimate?: S.Estimate; invoice?: S.Invoice }>; toKind: DocKind };
  api: {
    list: (p?: Record<string, string | number | undefined>) => Promise<{ rows: AnyDoc[]; total: number; pageCount: number }>;
    get: (id: string) => Promise<AnyDoc>;
    create: (b: Record<string, unknown>) => Promise<AnyDoc>;
    update: (id: string, b: Record<string, unknown>) => Promise<AnyDoc>;
    del: (id: string) => Promise<{ ok: boolean }>;
    send?: (id: string) => Promise<AnyDoc>;
  };
}

export const DOC_CONFIG: Record<DocKind, DocConfig> = {
  proposals: {
    kind: "proposals",
    titleKey: "sales.proposals.title",
    statuses: ["draft", "sent", "accepted", "declined"],
    hasTitle: true,
    hasValidUntil: true,
    hasIssueDate: false,
    hasDueDate: false,
    hasPayments: false,
    hasRecurrence: false,
    hasProject: false,
    canStartProject: false,
    canSend: true,
    convert: { labelKey: "sales.toEstimate", run: S.proposalToEstimate, toKind: "estimates" },
    api: {
      list: S.getProposals as DocConfig["api"]["list"],
      get: S.getProposal as DocConfig["api"]["get"],
      create: S.createProposal as DocConfig["api"]["create"],
      update: S.updateProposal as DocConfig["api"]["update"],
      del: S.deleteProposal,
      send: S.sendProposal as DocConfig["api"]["send"],
    },
  },
  estimates: {
    kind: "estimates",
    titleKey: "sales.estimates.title",
    statuses: ["draft", "sent", "accepted", "declined", "expired"],
    hasTitle: true,
    hasValidUntil: true,
    hasIssueDate: false,
    hasDueDate: false,
    hasPayments: false,
    hasRecurrence: false,
    hasProject: true,
    canStartProject: true,
    canSend: true,
    mailCompose: true,
    convert: { labelKey: "sales.toInvoice", run: S.estimateToInvoice, toKind: "invoices" },
    api: {
      list: S.getEstimates as DocConfig["api"]["list"],
      get: S.getEstimate as DocConfig["api"]["get"],
      create: S.createEstimate as DocConfig["api"]["create"],
      update: S.updateEstimate as DocConfig["api"]["update"],
      del: S.deleteEstimate,
      send: S.sendEstimate as DocConfig["api"]["send"],
    },
  },
  invoices: {
    kind: "invoices",
    titleKey: "sales.invoices.title",
    statuses: ["draft", "sent", "partially_paid", "paid", "overdue", "cancelled"],
    hasTitle: true,
    hasValidUntil: false,
    hasIssueDate: true,
    hasDueDate: true,
    hasPayments: true,
    hasRecurrence: true,
    hasProject: true,
    canStartProject: false,
    canSend: true,
    api: {
      list: S.getInvoices as DocConfig["api"]["list"],
      get: S.getInvoice as DocConfig["api"]["get"],
      create: S.createInvoice as DocConfig["api"]["create"],
      update: S.updateInvoice as DocConfig["api"]["update"],
      del: S.deleteInvoice,
      send: S.sendInvoice as DocConfig["api"]["send"],
    },
  },
  "credit-notes": {
    kind: "credit-notes",
    titleKey: "sales.creditNotes.title",
    statuses: ["draft", "issued", "applied"],
    hasTitle: false,
    hasValidUntil: false,
    hasIssueDate: false,
    hasDueDate: false,
    hasPayments: false,
    hasRecurrence: false,
    hasProject: false,
    canStartProject: false,
    canSend: false,
    api: {
      list: S.getCreditNotes as DocConfig["api"]["list"],
      get: S.getCreditNote as DocConfig["api"]["get"],
      create: S.createCreditNote as DocConfig["api"]["create"],
      update: S.updateCreditNote as DocConfig["api"]["update"],
      del: S.deleteCreditNote,
    },
  },
};

/** Normalised i18n segment (the kind "credit-notes" becomes the key "creditNotes"). */
export const KIND_I18N: Record<DocKind, string> = {
  proposals: "proposals",
  estimates: "estimates",
  invoices: "invoices",
  "credit-notes": "creditNotes",
};

export const DOC_TABS: { kind: DocKind; labelKey: string }[] = [
  { kind: "proposals", labelKey: "sales.proposals.title" },
  { kind: "estimates", labelKey: "sales.estimates.title" },
  { kind: "invoices", labelKey: "sales.invoices.title" },
  { kind: "credit-notes", labelKey: "sales.creditNotes.title" },
];
